import UUIDBase from '../UUIDBase'
import Category from './Category'
import Brand from './Brand'
import Tag from './Tag'
import Vendor from '@/models/vendor/Vendor'
import Stock from './Stock'
import { localizeFieldName, useI18nInstance } from '@/plugins/i18n'
import Dinero, { Currency, Dinero as DineroType } from 'dinero.js'
import CustomPriceType from './CustomPrice'
import Attribute from './Attribute'
import ItemPack from "@/models/inventory/ItemPack";
import ItemCustomPrice from "@/models/inventory/ItemCustomPrice";
import ItemImage from "@/models/inventory/ItemImage";
import {UploadUserFile} from "element-plus";
import {
  inventoryReceivingI18nMessages,
  InventoryReceivingItemSerial
} from "@/models/inventory/InventoryReceiving";

const { t } = useI18nInstance().global

export const itemI18nMessages = {
  en: {
    name: 'Item Name',
    receiptName: 'Receipt Name',
    description: 'Item Description',

    barcode: 'Barcode',
    sku: 'SKU',

    price: 'Price',
    cost: 'Cost',
    dynamicCost: 'Dynamic Cost',

    imageURL: 'Item Image',

    isSerialized: 'Serialized',
    discountEligible: 'Discount Eligible',
    unlimitedStock: 'Unlimited / Service',
    sellOutOfStock: 'Sell out of Stock',
    disabled: 'Disabled',

    expirationDate: 'Expiration Date',

    vendor: 'Vendor',
    itemBrand: 'Brand',
    itemCategory: 'Category',
    itemTags: 'Tags',

    itemStocks: 'Stocks',
    currentLocationStockQuantity: 'Quantity',//'Stock quantity (in this store)',

    listPriceAmount: 'List Price',
    salePriceAmount: 'Sale Price',
    wholesalePriceAmount: 'Wholesale Price',

    priceAmount: 'Price',
    priceType: 'Price Type',
    static: 'Static',
    dynamic: 'Dynamic',
    isImported: 'Imported?'
  },

  ar: {
    name: 'اسم المنتج',
    receiptName: 'اسم المنتج في الفاتورة',
    description: 'وصف المنتج',

    barcode: 'Barcode',
    sku: 'SKU',

    price: 'السعر',
    cost: 'التكلفة',
    dynamicCost: 'التكلفة الديناميكية',

    imageURL: 'صورة المنتج',

    isSerialized: 'منتج مرقم - serialized',
    discountEligible: 'قابل للتخفيض',
    unlimitedStock: 'غير محدود / خدمة',
    sellOutOfStock: 'بيع بعد نفاد المخزون',
    disabled: 'معطل',
    expirationDate: 'تاريخ النفاذ',

    vendor: 'المورّد',
    itemBrand: 'العلامة التجارية',
    itemCategory: 'المجموعة',
    itemTags: 'الوسوم',

    itemStocks: 'المخزون',
    currentLocationStockQuantity: 'الكمية' ,//'المخزون في هذا الموقع',

    listPriceAmount: 'سعر القائمة',
    salePriceAmount: 'سعر البيع',
    wholesalePriceAmount: 'سعر الجملة',

    priceAmount: 'السعر',
    priceType: 'نوع السعر',
    static: 'ثابت',
    dynamic: 'متغير',
    isImported: 'مستورد؟'
  }
}
//
// export class ItemCustomPrice {
//   id?: string
//   priceAmount?: number = 0
//   priceDinero?: DineroType
//   customPriceType?: CustomPriceType
// }

export interface ItemBarcode {
  id?: string
  barcode: string
  description?: string
  itemID?: string
}

export interface ItemPaginated {
  data: Array<Item>,
  total: number
}

export default class Item extends UUIDBase {
  constructor() {
    super();
    this.requestOnlyQuantityInHandItemSerials = new Array<InventoryReceivingItemSerial>()
  }

  static from(json: Record<string, unknown> | Item) {
    const item = Object.assign(new Item(), json)

    if (item.listPriceAmount && item.listPriceCurrency) {
      item.listPriceDinero = Dinero({
        amount: item.listPriceAmount,
        currency: item.listPriceCurrency as Currency,
        precision: item.listPricePrecision
      })
    }

    if (item.salePriceAmount && item.salePriceCurrency) {
      item.salePriceDinero = Dinero({
        amount: item.salePriceAmount,
        currency: item.salePriceCurrency as Currency,
        precision: item.salePricePrecision
      })
    }

    if (item.wholesalePriceAmount && item.wholesalePriceCurrency) {
      item.wholesalePriceDinero = Dinero({
        amount: item.wholesalePriceAmount,
        currency: item.wholesalePriceCurrency as Currency,
        precision: item.wholesalePricePrecision
      })
    }

    if (item.costAmount && item.costCurrency) {
      item.costDinero = Dinero({
        amount: item.costAmount,
        currency: item.costCurrency as Currency,
        precision: item.costPrecision
      })
    }    
    
    if (json.variants) {
      item.variants = json.variants.map(i => Item.from(i));
    }


    if (json.itemPacks) {
      item.itemPacks = json.itemPacks.map(i => ItemPack.from(i));
    }

    if (json.itemImages) {
      item.itemImages = json.itemImages.map(i => ItemImage.from(i));
    }

    if (json.itemCustomPrices) {
      item.itemCustomPrices = json.itemCustomPrices.map(i => ItemCustomPrice.from(i));
    }

    if (json.itemBarcodes) {
      item.itemBarcodes = json.itemBarcodes as ItemBarcode[];
    }

    item.createdAt = new Date(json.createdAt as string)
    item.updatedAt = new Date(json.updatedAt as string)
    item.deletedAt = json.deletedAt ? new Date(json.deletedAt as string) : undefined
    return item
  }

  static ENDPOINT = '/inventory/items'

  name = ''
  receiptName = ''
  isImported = false
  description = ''
  minQty?: number

  itemGroupID?: string
  itemParentID?: string
  hasVariants?: string
  variants = new Array<Item>()

  itemImages = new Array<ItemImage>()
  itemPacks = new Array<ItemPack>()
  itemCustomPrices = new Array<ItemCustomPrice>()
  itemBarcodes = new Array<ItemBarcode>()

  toUploadImages: Array<{itemID: string, isDefault: boolean, data: UploadUserFile}> = [];

  options: Record<string, string> = {}
  newAttributes: Array<any> = [];
  barcode?: string
  sku?: string
  newBarcodes = new Array<ItemBarcode>()
  priceType?: string = 'static'

  listPriceAmount?: number
  listPriceCurrency?: string
  listPricePrecision?: number
  listPriceDinero?: DineroType

  salePriceAmount?: number
  salePriceCurrency?: string
  salePricePrecision?: number
  salePriceDinero?: DineroType

  wholesalePriceAmount?: number
  wholesalePriceCurrency?: string
  wholesalePricePrecision?: number
  wholesalePriceDinero?: DineroType

  costAmount?: number
  costCurrency?: string
  costPrecision?: number
  costDinero?: DineroType

  currentDynamicCostAmount?: number

  imageURL?: string
  imageType?: string

  discountEligible = true
  isSerialized = false
  isExpirable = false
  unlimitedStock = false
  sellOutOfStock = true
  disabled = false

  expirationDate = new Date()

  // belongs to
  vendor: Vendor = new Vendor()
  vendorID: string;
  itemBrand: Brand = new Brand()
  itemBrandID: string;
  itemCategory: Category = new Category()
  itemCategoryID: string;

  itemTags = new Array<Tag>()
  itemAttributes = new Array<Attribute>()

  currentLocationStock = new Stock()
  itemStocks = new Array<Stock>()
  currentLocationStockQuantity = 0

  requestOnlyQuantityInHand?: Number;
  requestOnlyQuantityInHandItemSerials?: InventoryReceivingItemSerial[]
  // customPrices = new Array<ItemCustomPrice>()
  requestOnlyBatchNumber?: string
  requestOnlyExpiryDate?: Date
  requestOnlyManufactureDate?: Date

  insertSerialNumber(serialNumber: string) {
    this.requestOnlyQuantityInHandItemSerials?.push(new InventoryReceivingItemSerial(serialNumber))
  }

  removeSerialNumber(index: number) {
    this.requestOnlyQuantityInHandItemSerials?.splice(index, 1)
  }


  validateSerials() {
    if (this.isSerialized && this.requestOnlyQuantityInHandItemSerials?.length != this.requestOnlyQuantityInHand) {
      return t('validation.matchingLength', {
        thisField: localizeFieldName('serialNumbers', inventoryReceivingI18nMessages),
        thatField: localizeFieldName('quantity', inventoryReceivingI18nMessages)
      })
    }
  }
  // validation
  validateName() {
    if (this.name.length === 0) {
      return t('validation.required', {
        field: localizeFieldName('name', itemI18nMessages)
      })
    } else if (this.name.length > 255) {
      return t('validation.maxLength', {
        field: localizeFieldName('name', itemI18nMessages)
      })
    }
  }

  validatePrice() {
    if (this?.listPriceAmount && this.listPriceAmount < 0) {
      return t('validation.nonNegative', {
        field: localizeFieldName('price', itemI18nMessages)
      })
    }
  }

  validateImage() {
    if (
      this.imageType &&
      this.imageType !== 'jpeg' &&
      this.imageType !== 'jpg' &&
      this.imageType !== 'png'
    ) {
      return t('validation.unsupportedImage', {
        field: localizeFieldName('imageURL', itemI18nMessages)
      })
    }
  }
}
